import React, { useState } from 'react';
import { ArrowTopRightOnSquareIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { ragAPI } from '../../services/api';
import { trackOnboarding } from '../../services/onboardingTelemetry';

const MIN_PASTED_CHARACTERS = 80;

const sourceFilename = (sourceUrl) => {
  try {
    const host = new URL(sourceUrl).hostname.replace(/^www\./, '');
    const safeHost = host.replace(/[^a-z0-9.-]+/gi, '-').replace(/^-+|-+$/g, '');
    return `${safeHost || 'website'}-pasted-content.txt`;
  } catch (error) {
    return 'website-pasted-content.txt';
  }
};

const PasteContentRecovery = ({ sourceUrl, collectionId, onSaved, standalone = false, initialContent = '', resolveCollection }) => {
  const [content, setContent] = useState(initialContent);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const save = async (event) => {
    event.preventDefault();
    const cleanContent = content.trim();
    if (cleanContent.length < MIN_PASTED_CHARACTERS) {
      setFeedback({
        type: 'error',
        message: 'Paste at least a paragraph of useful business information before adding it.',
      });
      return;
    }
    if (!collectionId && !resolveCollection) {
      setFeedback({ type: 'error', message: 'Choose a knowledge collection first.' });
      return;
    }

    setSaving(true);
    setFeedback(null);
    try {
      const body = `Source: ${sourceUrl || 'Pasted website content'}\n\n${cleanContent}`;
      const file = new File([body], sourceFilename(sourceUrl), { type: 'text/plain' });
      const formData = new FormData();
      formData.append('file', file);
      // Setup creates its collection now that there is something to put in it.
      const targetId = resolveCollection ? (await resolveCollection()).id : collectionId;
      const response = await ragAPI.uploadDocument(targetId, formData);
      const payload = response.data || response;
      const document = payload.document || {};
      const health = document.ingest_health;
      const chunks = Number(document.chunks || 0);

      // Only nothing searchable is refused here. Whether one short section is
      // enough is the server's decision, shown before the agent exists:
      // refusing short, useful text at this point turned real businesses away.
      if (chunks < 1) {
        // The upload itself returned 200, so without this the request log
        // shows a success for a customer who was just told it failed.
        trackOnboarding('knowledge_rejected', {
          method: 'paste',
          verdict: health?.verdict || 'empty',
          chunks,
          characters: cleanContent.length,
        });
        setFeedback({
          type: 'error',
          message: health?.reason || 'That text did not create usable knowledge. Paste more detail and try again.',
        });
        return;
      }

      setFeedback({
        type: 'success',
        message: `Content added — ${chunks} searchable section${chunks === 1 ? '' : 's'} created.`,
      });
      setContent('');
      onSaved?.(payload);
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Could not add the pasted content.';
      setFeedback({
        type: 'error',
        message: typeof detail === 'string' ? detail : 'Could not add the pasted content.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section aria-labelledby="paste-content-title" className="rounded-2xl border border-blue-200 bg-blue-50 p-4 shadow-sm sm:p-6">
      <div className="flex items-start gap-3">
        <ClipboardDocumentIcon className="mt-0.5 h-7 w-7 shrink-0 text-blue-700" />
        <div>
          <h3 id="paste-content-title" className="text-lg font-bold text-gray-950">
            {standalone ? (initialContent ? 'Check your business information' : 'Add your business information') : 'Copy the page text into Serviti'}
          </h3>
          <p className="mt-1 text-sm leading-6 text-gray-700">
            {standalone
              ? <>Use this when you do not have a website or want the fastest setup. Paste your <strong className="font-semibold text-gray-950">services, prices, hours, locations, policies, and FAQs</strong>.</>
              : 'The page is visible to you, but Serviti could not read enough of it automatically. Copy the useful text and paste it below.'}
          </p>
        </div>
      </div>

      {sourceUrl && (
        <a
          href={sourceUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg border border-blue-300 bg-white px-4 py-2.5 text-sm font-semibold text-blue-800 hover:bg-blue-100 sm:w-auto"
        >
          Open your website
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </a>
      )}

      {!standalone && <ol className="mt-4 grid gap-3 text-sm text-gray-800 sm:grid-cols-3">
        <li className="rounded-xl bg-white p-3">
          <span className="font-bold text-blue-700">1. Select</span>
          <p className="mt-1 leading-5"><strong>Phone:</strong> touch and hold the text, then choose Select all.</p>
          <p className="mt-1 leading-5"><strong>Computer:</strong> press Ctrl+A (Windows) or Cmd+A (Mac).</p>
        </li>
        <li className="rounded-xl bg-white p-3">
          <span className="font-bold text-blue-700">2. Copy</span>
          <p className="mt-1 leading-5"><strong>Phone:</strong> tap Copy.</p>
          <p className="mt-1 leading-5"><strong>Computer:</strong> press Ctrl+C or Cmd+C.</p>
        </li>
        <li className="rounded-xl bg-white p-3">
          <span className="font-bold text-blue-700">3. Paste</span>
          <p className="mt-1 leading-5"><strong>Phone:</strong> return here, touch and hold the box, then tap Paste.</p>
          <p className="mt-1 leading-5"><strong>Computer:</strong> press Ctrl+V or Cmd+V in the box.</p>
        </li>
      </ol>}

      <form onSubmit={save} className="mt-5">
        <label htmlFor="pasted-website-content" className="block text-sm font-semibold text-gray-900">
            {standalone ? 'Business information' : 'Paste website text'}
        </label>
        <div role="note" className="mt-2 rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3">
          <p className="text-sm leading-6 text-amber-950">
            Include <strong className="font-bold">services, prices, opening hours, locations, policies, and common questions</strong>. You can paste several pages together.
          </p>
        </div>
        {standalone && !content && (
          <div className="mt-2 text-xs font-bold uppercase tracking-wide text-amber-800">Start here</div>
        )}
        <textarea
          id="pasted-website-content"
          value={content}
          onChange={(event) => setContent(event.target.value)}
          rows={9}
          // Only the standalone wizard step owns the focus. Inside the crawler
          // this component is a recovery panel rendered *below* a live URL
          // field, and grabbing focus there would yank the cursor out of it.
          autoFocus={standalone}
          autoCapitalize="sentences"
          placeholder="Touch and hold here, then tap Paste…"
          className={`mt-2 min-h-[220px] w-full rounded-xl bg-white px-4 py-3 text-base leading-6 text-gray-950 caret-blue-600 shadow-inner focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            standalone && !content
              ? 'border-2 border-amber-400 ring-4 ring-amber-100'
              : 'border border-gray-300'
          }`}
        />

        {feedback && (
          <div
            role={feedback.type === 'error' ? 'alert' : 'status'}
            className={`mt-3 rounded-lg px-4 py-3 text-sm font-medium ${
              feedback.type === 'error'
                ? 'border border-red-200 bg-red-50 text-red-800'
                : 'border border-green-200 bg-green-50 text-green-800'
            }`}
          >
            {feedback.message}
          </div>
        )}

        <button
          type="submit"
          disabled={saving || content.trim().length < MIN_PASTED_CHARACTERS}
          className="mt-4 min-h-[48px] w-full rounded-xl bg-blue-700 px-5 py-3 text-base font-semibold text-white shadow-sm hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
        >
          {saving ? 'Adding content…' : 'Add pasted content to my agent'}
        </button>
      </form>
    </section>
  );
};

export default PasteContentRecovery;
